// src/pages/Login.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 20.04.2025 to fix undefined token saving issue and add response validation
// Updated by Grok on 21.04.2025 to ensure proper token parsing and storage
// Updated by Claude on 19.04.2025 to fix token handling
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserLanguage } from '../utils/language';
import { TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, IMAGE_BASE_URL } from '../config';

const Login = () => {
  const savedEmail = localStorage.getItem('LOGIN_EMAIL') || '';
  const savedPassword = localStorage.getItem('LOGIN_PASSWORD') ? atob(localStorage.getItem('LOGIN_PASSWORD')) : '';

  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState(savedPassword);
  const [loginError, setLoginError] = useState(null);
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const language = getUserLanguage();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    document.title = language === 'cs'
      ? 'Přihlášení | NO RISK NO DRIFT Admin'
      : 'Login | NO RISK NO DRIFT Admin';
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError(null);
    try {
      // Získáme celou odpověď z funkce login
      const response = await login(email, password);
      console.log('[Login] Full login response:', response);
      
      // Zkontrolujeme, zda odpověď není null/undefined
      if (!response) {
        throw new Error('Přihlášení selhalo: prázdná odpověď');
      }
      
      // Zkontrolujeme různé možné struktury odpovědi
      const accessToken = 
        response.accessToken || 
        (response.data && response.data.accessToken) || 
        response.token;
        
      const refreshToken = 
        response.refreshToken || 
        (response.data && response.data.refreshToken) || 
        response.refresh_token;
      
      if (!accessToken || !refreshToken) {
        console.error('[Login] Tokens not found in response', response);
        throw new Error('Přihlášení selhalo: chybějící tokeny');
      }
      
      // Uložíme tokeny (přestože by už měly být uloženy v auth.js)
      localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      localStorage.setItem('LOGIN_EMAIL', email);
      localStorage.setItem('LOGIN_PASSWORD', btoa(password));
      
      console.log('[Login] Token saved:', accessToken.slice(0, 20) + '...');
      console.log('[Login] Refresh token saved:', refreshToken.slice(0, 20) + '...');
      
      navigate('/admin');
    } catch (error) {
      console.error('[Login] Error:', error);
      setLoginError(
        language === 'cs'
          ? 'Nesprávné přihlašovací údaje nebo chyba serveru. Zkuste to znovu.'
          : 'Incorrect login credentials or server error. Please try again.'
      );
    }
  };

  if (loading) {
    return (
      <div className="admin-interface admin-min-h-screen admin-bg-black admin-flex admin-items-center admin-justify-center">
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-interface admin-min-h-screen admin-bg-black admin-flex admin-flex-col admin-items-center admin-justify-center admin-px-4">
      <div className="admin-w-full admin-max-w-md">
        <div className="admin-text-center admin-mb-8">
          <Link to="/">
            <img
              src={`${IMAGE_BASE_URL}/images/logo-A2.png`}
              alt="NO RISK NO DRIFT Logo"
              className="admin-h-16 admin-w-auto admin-mx-auto"
            />
          </Link>
          <h1 className="admin-text-2xl admin-font-title admin-font-bold admin-text-white admin-mt-6">
            {language === 'cs' ? 'Přihlášení do administrace' : 'Admin Login'}
          </h1>
        </div>
        <div className="admin-bg-dark-gray admin-rounded-lg admin-shadow-lg admin-p-8">
          {loginError && (
            <div className="admin-bg-red-600/20 admin-border admin-border-red-600/30 admin-rounded-md admin-p-4 admin-mb-6">
              <p className="admin-text-red-400 admin-text-sm">{loginError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="admin-mb-6">
              <label htmlFor="email" className="admin-form-label">
                {language === 'cs' ? 'E-mail' : 'Email'}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="admin-form-input"
                required
              />
            </div>
            <div className="admin-mb-6">
              <label htmlFor="password" className="admin-form-label">
                {language === 'cs' ? 'Heslo' : 'Password'}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="admin-form-input"
                required
              />
            </div>
            <button
              type="submit"
              className="admin-btn admin-btn-primary admin-btn-block"
              disabled={loading}
            >
              {loading 
                ? (language === 'cs' ? 'Přihlašování...' : 'Logging in...') 
                : (language === 'cs' ? 'Přihlásit se' : 'Log In')}
            </button>
          </form>
          <div className="admin-mt-6 admin-text-center">
            <Link to="/" className="admin-text-gray-400 hover:admin-text-red-500 admin-text-sm admin-transition">
              {language === 'cs' ? 'Zpět na hlavní stránku' : 'Back to Homepage'}
            </Link>
          </div>
        </div>
        <div className="admin-mt-8 admin-text-center admin-text-gray-500 admin-text-xs">
          © {new Date().getFullYear()} NO RISK NO DRIFT.{' '}
          {language === 'cs' ? 'Všechna práva vyhrazena.' : 'All rights reserved.'}
        </div>
      </div>
    </div>
  );
};

export default Login;